import { useApolloClient, useQuery as useGQLQuery } from '@apollo/client';
import { Box, Grid, Paper } from '@mui/material';

import { useMemo } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import QUERY from 'gql/queries/ModulesService/getStatesModuleSelections.gql';
import USER_QUERY from 'gql/queries/currentUser.gql';

import { useCoursesQuery } from 'api/hooks/useCoursesQuery';
import { GQLGuard, ErrorBoundary } from 'app/components';
import { BackToTopButton } from 'app/components/BackToTopButton';
import { TilesOrListView, TypedGridColumns } from 'app/components/TilesOrListView';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { useCurrentURL } from 'utils/hooks/useCurrentURL';
import usePageHeading from 'utils/hooks/usePageHeading';

import {
  ModuleSelectionCard,
  ModuleSelectionWithCourses
} from '../../ModuleSelections/ModuleSelectionsList';
import '../../ModuleSelections/ModuleSelectionsList/styles/ModuleSelections.scss';
import {
  Attachment,
  Maybe,
  ModuleStudentSelection as ModuleStudentSelectionType,
  State,
  SubjectType
} from '../../ModuleSelections/models';
import { studentSemesterParser } from '../../helper';
import { Lottery } from '../models';

export class ModuleStudentSelection implements ModuleStudentSelectionType {
  public id: string;
  public subjectType: SubjectType;
  public title: string;
  public generalNotes?: string;
  public electionTip?: string;
  public attachments?: Maybe<Attachment[]>;
  public startDate?: Date;
  public endDate?: Date;
  public contentVisibleFrom?: Date;
  public dischargeByStudentsUntil?: Date;
  public lengthOfTheWishlist: number;
  public maxSelectedModules: number;
  public activeInAllCommunities?: boolean;
  public state: State;
  public createdByCourseId?: number;
  public participatingCourseIds?: Maybe<number[]>;
  public courseId: number;
  public lottery?: Lottery;
  public publishedAt: Date;
  constructor({
    id,
    subjectType,
    title,
    generalNotes,
    electionTip,
    attachments,
    startDate,
    endDate,
    contentVisibleFrom,
    dischargeByStudentsUntil,
    lengthOfTheWishlist,
    maxSelectedModules,
    activeInAllCommunities,
    state,
    createdByCourseId,
    participatingCourseIds,
    courseId,
    lottery,
    publishedAt
  }: ModuleStudentSelection) {
    this.id = id;
    this.subjectType = subjectType;
    this.title = title;
    this.generalNotes = generalNotes;
    this.electionTip = electionTip;
    this.attachments = attachments;
    this.lengthOfTheWishlist = lengthOfTheWishlist;
    this.maxSelectedModules = maxSelectedModules;
    this.activeInAllCommunities = activeInAllCommunities;
    this.state = state;
    this.startDate = startDate;
    this.dischargeByStudentsUntil = dischargeByStudentsUntil;
    this.endDate = endDate;
    this.contentVisibleFrom = contentVisibleFrom;
    this.createdByCourseId = createdByCourseId;
    this.participatingCourseIds = participatingCourseIds;
    this.courseId = courseId;
    this.lottery = lottery;
    this.publishedAt = publishedAt;
  }
}

interface Props {
  subjectType: SubjectType;
}
export const ModuleSelectionsStudentView = (props: Props) => {
  const { t } = useTranslation();
  const client = useApolloClient();
  const userData = client.readQuery({ query: USER_QUERY });
  const user = userData.currentUser;
  const navigate = useNavigate();
  const currentURL = useCurrentURL();
  usePageHeading({
    heading: t(translations.pages.moduleSelections.title[props.subjectType.toLowerCase()]()),
    mobilePadding: false
  });

  const { data: courses } = useCoursesQuery();

  const getStateModuleSelectionsQuery = useGQLQuery<any, any>(QUERY, {
    context: { clientName: 'federation' },
    variables: {
      states: [State.Active, State.Completed, State.Dischargable, State.Started],
      subjectType: props.subjectType,
      courses: user.courses.map(c => Number(c.id)),
      semester: user.relationships.map(relation =>
        studentSemesterParser(relation.degree, relation.semester)
      )
    }
  });
  const dispatch = useDispatch();

  const columns = useMemo((): TypedGridColumns<ModuleSelectionWithCourses> => {
    return [
      {
        field: 'title',
        flex: 2,
        minWidth: 200,
        headerName: t(translations.pages.moduleSelections.create.generalInfo.title())
      },
      {
        field: 'startDate',
        flex: 2,
        headerName: t(translations.pages.moduleSelections.create.periodsAndExtraInfo.startDate()),
        valueGetter: (value, row) =>
          row.startDate
            ? new Date(row.startDate).toLocaleDateString('de-DE', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
              })
            : '-/-'
      },
      {
        field: 'endDate',
        flex: 1,
        headerName: t(translations.pages.moduleSelections.create.periodsAndExtraInfo.endDate()),
        valueGetter: (value, row) =>
          row.endDate
            ? new Date(row.endDate).toLocaleDateString('de-DE', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
              })
            : '-/-'
      },
      {
        field: 'dischargeByStudentsUntil',
        flex: 2,
        headerName: t(
          translations.pages.moduleSelections.create.periodsAndExtraInfo.dischargeByStudentsUntil()
        ),
        valueGetter: (value, row) =>
          row.dischargeByStudentsUntil
            ? new Date(row.dischargeByStudentsUntil).toLocaleString('de-DE', {
                day: 'numeric',
                month: 'numeric',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })
            : '-/-'
      },
      {
        field: 'createdByCourse',
        valueGetter: (value, row) => row.createdByCourse,
        flex: 3,
        headerName: t(translations.pages.moduleSelections.createdBy())
      },
      {
        field: 'participatingCourseIds',
        valueGetter: (value, row) => row.participatingCourseIds?.join(', '),
        flex: 3,
        headerName: t(translations.pages.moduleSelections.participants())
      }
    ];
  }, [t]);

  const mapModuleSelectionsFromData = (moduleSelections: any[]): ModuleSelectionWithCourses[] => {
    return moduleSelections.map(moduleSelection => {
      return new ModuleSelectionWithCourses({
        ...moduleSelection,
        createdByCourse: courses.find(item => item.id === moduleSelection.courseId)?.name,
        participatingCourseIds: courses
          ?.filter(course => moduleSelection.participatingCourseIds?.includes(course.id))
          .map(filteredCourse => filteredCourse.shorthand)
      });
    });
  };

  const Body = () => (
    <Box component={Paper}>
      <Grid container className="module-selections">
        <ErrorBoundary boundaryTag="moduleSelectionsListBoundary">
          <GQLGuard
            query={[getStateModuleSelectionsQuery]}
            onError={error => {
              if (error.graphQLErrors[0]?.extensions?.status === 404) {
                dispatch(
                  AlertActions.error(`${t(translations.pages.moduleSelections.failedLoading())}`)
                );
              } else {
                dispatch(
                  AlertActions.error(
                    `${t(translations.pages.moduleSelections.failedLoading())}: ${error.message}`
                  )
                );
              }
              navigate('/', { replace: true });
              return null;
            }}
          >
            {([data]: [{ getStatesModuleSelections }]) => {
              if (data.getStatesModuleSelections?.length === 1) {
                navigate(
                  `/${props.subjectType.toLowerCase()}/student/module-selections/${
                    data.getStatesModuleSelections[0].id
                  }`,
                  { state: { back: '/' } }
                );
              }

              const moduleSelections = mapModuleSelectionsFromData(data.getStatesModuleSelections);

              return (
                <Grid style={{ width: '100%', margin: '1rem' }}>
                  <TilesOrListView
                    data={moduleSelections}
                    columns={columns}
                    height={'80vh'}
                    renderTile={item => (
                      <ModuleSelectionCard
                        moduleSelection={item}
                        subjectType={props.subjectType}
                        hideButtons={true}
                        isStudentView={true}
                      />
                    )}
                    onRowClick={event => {
                      navigate(
                        `/${event.row.subjectType.toLowerCase()}/student/module-selections/${
                          event.row.id
                        }`,
                        { state: { back: currentURL } }
                      );
                    }}
                  />
                </Grid>
              );
            }}
          </GQLGuard>
        </ErrorBoundary>
      </Grid>
    </Box>
  );

  return (
    <>
      <Body />
      {isMobile && <BackToTopButton />}
    </>
  );
};
